import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { ProgressionTemplate, ProgressionTemplateSet } from "../../libs/progression/types";
import { parseRepsInput, formatRepsDisplay } from "../../libs/progression/utils";
import { SubmitButton, DeleteButton, PlusButton } from "../Button";
import { useGetAllProgressionContainersQuery, useCreateProgressionContainerMutation } from "../../libs/progression/api";

type SetFormValues = {
  repsInput: string;
  rest: number;
};

type WeekFormValues = {
  sets: SetFormValues[];
  notes: string;
};

type FormValues = {
  name: string;
  numWeeks: number;
  weeks: WeekFormValues[];
};

const DEFAULT_NUM_WEEKS = 5;
const DEFAULT_SET: SetFormValues = { repsInput: "10", rest: 60 };

const buildDefaultWeeks = (numWeeks: number): WeekFormValues[] =>
  Array.from({ length: numWeeks }, () => ({ sets: [{ ...DEFAULT_SET }], notes: "" }));

const templateToFormValues = (template: ProgressionTemplate): FormValues => ({
  name: template.name,
  numWeeks: template.numWeeks,
  weeks: Array.from({ length: template.numWeeks }, (_, i) => {
    const week = template.weeks.find((w) => w.weekNumber === i + 1);
    return {
      sets: week
        ? week.sets.map((s) => ({
            repsInput: formatRepsDisplay(s.reps, s.repsRange),
            rest: s.rest,
          }))
        : [{ ...DEFAULT_SET }],
      notes: week?.notes || "",
    };
  }),
});

type Props = {
  defaultValues?: ProgressionTemplate;
  onSubmit: (template: Omit<ProgressionTemplate, "id">) => void;
  isPending: boolean;
  defaultContainerId?: number;
};

export const ProgressionForm: React.FC<Props> = ({ defaultValues, onSubmit, isPending, defaultContainerId }) => {
  const [activeWeekTab, setActiveWeekTab] = useState(0);
  const [copyFrom, setCopyFrom] = useState(1);
  const [selectedContainerId, setSelectedContainerId] = useState<number | undefined>(
    defaultValues?.idContainer ?? defaultContainerId
  );
  const [newContainerName, setNewContainerName] = useState("");
  const [showNewContainerInput, setShowNewContainerInput] = useState(false);
  const { data: allContainers } = useGetAllProgressionContainersQuery();
  const [createContainer] = useCreateProgressionContainerMutation();

  const { register, control, watch, handleSubmit, setValue, reset } = useForm<FormValues>({
    defaultValues: defaultValues
      ? templateToFormValues(defaultValues)
      : {
          name: "",
          numWeeks: DEFAULT_NUM_WEEKS,
          weeks: buildDefaultWeeks(DEFAULT_NUM_WEEKS),
        },
  });

  const watchNumWeeks = watch("numWeeks");
  const watchWeeks = watch("weeks");

  const otherWeeks = Array.from({ length: watchNumWeeks }, (_, i) => i).filter((i) => i !== activeWeekTab);

  const onNumWeeksChange = (newNum: number) => {
    const current = watchWeeks || [];
    const updated = Array.from({ length: newNum }, (_, i) => {
      if (current[i]) return current[i];
      const prev = current[i - 1] ?? current[current.length - 1];
      return prev
        ? { sets: prev.sets.map((s) => ({ ...s })), notes: prev.notes || "" }
        : { sets: [{ ...DEFAULT_SET }], notes: "" };
    });
    setValue("numWeeks", newNum);
    setValue("weeks", updated);
    setActiveWeekTab(Math.min(activeWeekTab, newNum - 1));
  };

  const handleCopyAll = () => {
    const source = (watchWeeks || [])[copyFrom];
    if (source) {
      setValue(`weeks.${activeWeekTab}.sets`, source.sets.map((s) => ({ ...s })), { shouldDirty: true });
      setValue(`weeks.${activeWeekTab}.notes`, source.notes || "", { shouldDirty: true });
    }
  };

  const handleCopyNoteOnly = () => {
    const source = (watchWeeks || [])[copyFrom];
    if (source) {
      setValue(`weeks.${activeWeekTab}.notes`, source.notes || "", { shouldDirty: true });
    }
  };

  const handleCreateContainerInline = async () => {
    if (!newContainerName.trim()) return;
    try {
      const result = await createContainer({ name: newContainerName.trim() }).unwrap();
      setSelectedContainerId(result.id);
      setNewContainerName("");
      setShowNewContainerInput(false);
    } catch {}
  };

  const handleSubmitForm = (data: FormValues) => {
    const template: Omit<ProgressionTemplate, "id"> = {
      name: data.name,
      numWeeks: data.numWeeks,
      idContainer: selectedContainerId,
      weeks: data.weeks.map((week, i) => ({
        weekNumber: i + 1,
        sets: week.sets.map((s): ProgressionTemplateSet => {
          const { reps, repsRange } = parseRepsInput(s.repsInput);
          return { reps, repsRange, rest: Number(s.rest) };
        }),
        notes: week.notes || "",
      })),
    };
    onSubmit(template);
  };

  // Sync copyFrom quando cambia tab
  const onTabChange = (i: number) => {
    setActiveWeekTab(i);
    setCopyFrom(i > 0 ? i - 1 : 1);
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit(handleSubmitForm)}>
      {/* Header: nome + settimane */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">Nome progressione</label>
          <input
            {...register("name", { required: true })}
            type="text"
            placeholder="Es. Neurale 5W"
            className="block w-full py-2.5 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">Contenitore</label>
          {showNewContainerInput ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newContainerName}
                onChange={(e) => setNewContainerName(e.target.value)}
                placeholder="Nome nuovo contenitore"
                className="block flex-1 py-2.5 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleCreateContainerInline(); }
                  if (e.key === "Escape") setShowNewContainerInput(false);
                }}
              />
              <button
                type="button"
                onClick={handleCreateContainerInline}
                className="px-3 py-2 text-sm text-white bg-primary-700 hover:bg-primary-800 rounded-lg"
              >
                Crea
              </button>
              <button
                type="button"
                onClick={() => setShowNewContainerInput(false)}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Annulla
              </button>
            </div>
          ) : (
            <select
              value={selectedContainerId ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "__new__") {
                  setShowNewContainerInput(true);
                } else {
                  setSelectedContainerId(val ? Number(val) : undefined);
                }
              }}
              className="block w-full py-2.5 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Seleziona contenitore --</option>
              {(allContainers || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="__new__">+ Nuovo contenitore...</option>
            </select>
          )}
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">Settimane</label>
          <select
            value={watchNumWeeks}
            onChange={(e) => onNumWeeksChange(Number(e.target.value))}
            className="block py-2.5 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            {[4, 5, 6].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Anteprima — solo visuale */}
      <ProgressionPreview weeks={watchWeeks || []} activeWeek={activeWeekTab} />

      {/* Pannello editor settimana */}
      <div>
        {/* Tab bar + copy controls */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-t-xl px-2 pt-2 flex items-end justify-between gap-2">
          {/* Tab settimane */}
          <div className="flex gap-1">
            {Array.from({ length: watchNumWeeks }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onTabChange(i)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
                  activeWeekTab === i
                    ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                Sett. {i + 1}
              </button>
            ))}
          </div>

          {/* Copy controls inline */}
          {otherWeeks.length > 0 && (
            <div className="flex items-center gap-1.5 pb-1.5 pr-1">
              <span className="text-gray-400 text-[0.65rem] whitespace-nowrap">Copia da</span>
              <select
                value={copyFrom}
                onChange={(e) => setCopyFrom(Number(e.target.value))}
                className="py-0.5 px-1.5 text-[0.65rem] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-gray-200 focus:ring-blue-500 focus:border-blue-500"
              >
                {otherWeeks.map((i) => (
                  <option key={i} value={i}>S{i + 1}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleCopyAll}
                className="px-2 py-0.5 text-[0.65rem] font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
              >
                Tutto
              </button>
              <button
                type="button"
                onClick={handleCopyNoteOnly}
                className="px-2 py-0.5 text-[0.65rem] font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Nota
              </button>
            </div>
          )}
        </div>

        {/* Contenuto settimana */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-t-0 rounded-b-xl p-5">
          <WeekSetsEditor
            key={activeWeekTab}
            weekIndex={activeWeekTab}
            numWeeks={watchNumWeeks}
            control={control}
            register={register}
            allWeeks={watchWeeks || []}
            setValue={setValue}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <SubmitButton isPending={isPending}>Salva progressione</SubmitButton>
        <button
          type="button"
          onClick={() => {
            reset();
            setActiveWeekTab(0);
          }}
          className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Reset
        </button>
      </div>
    </form>
  );
};

/* ──────────────────────────────────────────────
   Preview card-based
   ────────────────────────────────────────────── */

const ProgressionPreview: React.FC<{ weeks: WeekFormValues[]; activeWeek: number }> = ({ weeks, activeWeek }) => {
  if (!weeks.length) return null;

  return (
    <div>
      <label className="block mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Anteprima</label>
      <div className="overflow-x-auto">
        <div className="flex gap-2">
          {weeks.map((week, i) => (
            <div
              key={i}
              className={`flex-1 min-w-[110px] rounded-lg border text-[0.6rem] px-2.5 py-2 text-center ${
                i === activeWeek
                  ? "border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                  : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800"
              }`}
            >
              <div className="text-[0.55rem] font-semibold uppercase text-gray-400 mb-1">
                Sett. {i + 1}
              </div>
              {week.sets.length > 0 ? (
                <>
                  <div className="flex justify-between font-semibold mb-0.5">
                    <span>SET</span>
                    <span>REPS</span>
                    <span>REST</span>
                  </div>
                  {week.sets.map((set, si) => (
                    <div key={si} className="flex justify-between">
                      <span>{si + 1}°</span>
                      <span>{set.repsInput || "—"}</span>
                      <span>{set.rest || "—"}</span>
                    </div>
                  ))}
                </>
              ) : (
                <span className="text-gray-300">—</span>
              )}
              {week.notes && (
                <div className="mt-1.5 pt-1 border-t border-gray-100 text-[0.55rem] text-gray-400 italic line-clamp-2 text-left">
                  {week.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   Week editor (sets + notes + copy)
   ────────────────────────────────────────────── */

type WeekSetsEditorProps = {
  weekIndex: number;
  numWeeks: number;
  control: any;
  register: any;
  allWeeks: WeekFormValues[];
  setValue: any;
};

const WeekSetsEditor: React.FC<WeekSetsEditorProps> = ({
  weekIndex,
  numWeeks,
  control,
  register,
  allWeeks,
  setValue,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `weeks.${weekIndex}.sets`,
  });

  const handleCopyNoteToAll = () => {
    const currentNote = allWeeks[weekIndex]?.notes || "";
    for (let i = 0; i < numWeeks; i++) {
      if (i !== weekIndex) {
        setValue(`weeks.${i}.notes`, currentNote, { shouldDirty: true });
      }
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-5">
      {/* Colonna Set */}
      <div className="flex-1 min-w-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-gray-500 dark:text-gray-400">
                <th className="text-left px-2 py-1 w-12">Set</th>
                <th className="text-left px-2 py-1">Reps</th>
                <th className="text-left px-2 py-1">Rest (sec)</th>
                <th className="px-2 py-1 w-10" />
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => (
                <tr key={field.id} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="px-2 py-2 text-gray-400 font-medium">{i + 1}°</td>
                  <td className="px-2 py-2">
                    <input
                      {...register(`weeks.${weekIndex}.sets.${i}.repsInput`)}
                      type="text"
                      placeholder="10 o 10-12"
                      className="w-28 py-1.5 px-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-blue-500 focus:border-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      {...register(`weeks.${weekIndex}.sets.${i}.rest`, { valueAsNumber: true })}
                      type="number"
                      placeholder="60"
                      min={0}
                      className="w-20 py-1.5 px-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-blue-500 focus:border-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    {fields.length > 1 && (
                      <DeleteButton variant="icon" onClick={() => remove(i)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <PlusButton
            variant="default"
            onClick={() => {
              const currentSets = allWeeks[weekIndex]?.sets;
              const last = currentSets?.[currentSets.length - 1];
              append(last ? { ...last } : { ...DEFAULT_SET });
            }}
          >
            Aggiungi set
          </PlusButton>
        </div>
      </div>

      {/* Separatore visivo */}
      <div className="hidden lg:block w-px bg-gray-200 dark:bg-gray-700 self-stretch" />
      <div className="lg:hidden border-t border-gray-200 dark:border-gray-700" />

      {/* Colonna Note */}
      <div className="lg:w-[340px] flex-shrink-0">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Note</label>
          <button
            type="button"
            onClick={handleCopyNoteToAll}
            className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            Applica a tutte le settimane
          </button>
        </div>
        <textarea
          {...register(`weeks.${weekIndex}.notes`)}
          rows={4}
          placeholder="Es. intensità, RPE, indicazioni..."
          className="block w-full py-2 px-3 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
      </div>
    </div>
  );
};
